
import { IFinancingNeedItemReport, IFinancingNeedItemReportForm } from '@/types/processingSteps'
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import CharField from "@/components/processing_steps/commons/fields/CharField.vue"
import IconButton from "@/components/buttons/IconButton.vue"
import SingleSelectField from "@/components/processing_steps/commons/fields/SingleSelectField.vue"
import { API_URLS } from "@/utils/helpers"
import axios from "axios"
import DocumentField from "@/components/processing_steps/commons/fields/DocumentField.vue"
import { IPhoto } from "@/types/photos"
import ProcessingStepMixin from "@/components/processing_steps/ProcessingStepMixin.vue"
import ConfirmModal from "@/components/processing_steps/commons/modals/ConfirmModal.vue"

@Component({
  components: { ConfirmModal, DocumentField, SingleSelectField, IconButton, CharField }
})
export default class FinancingNeedsReport extends Mixins(ProcessingStepMixin) {
  @Prop() financingNeedReportItem!: IFinancingNeedItemReport | null
  @Prop({ default: "" }) resultReportSlug!: string
  @Prop({ default: false }) newItem!: boolean
  @Prop({ default: false }) isDisabled!: boolean

  isEditMode = false
  existingReportFiles: IPhoto[] = []
  userIsUploadingFile = false
  deleteModalOpen = false
  financingNeedReportItemFileUploadUrl = ""

  localFinancingNeedReportItem: IFinancingNeedItemReportForm = {
    title: '',
    description: '',
    expense_type: 1,
    expense_amount: 0,
    report_files: [],
    report_photos: [],
    file_album_slug: '',
    result_report_slug: ''
  }

  @Watch('financingNeedReportItem', { immediate: true, deep: true })
  onPropChange(newVal: IFinancingNeedItemReport|null) {
    if (!newVal) return
    this.localFinancingNeedReportItem = {
      title: newVal.title,
      description: newVal.description,
      expense_type: newVal.expense_type,
      expense_amount: newVal.expense_amount,
      report_files: newVal.report_files || [],
      report_photos: newVal.report_photos || [],
      file_album_slug: newVal.file_album_slug,
      result_report_slug: this.resultReportSlug
    }
    this.existingReportFiles = [
      ...(newVal.report_files || []),
      ...(newVal.report_photos || [])
    ]
    this.setFinancingNeedReportItemFileUploadUrl(newVal.file_album_slug)
  }

  get requiredFieldsNotFilled () {
    return !this.localFinancingNeedReportItem.title ||
      !this.localFinancingNeedReportItem.description ||
      this.localFinancingNeedReportItem.expense_amount <= 0
  }

  get isUpdate () {
    return !!this.financingNeedReportItem.id
  }

  get expenseTypeOptions() {
    return [
      { value: '1', text: this.$gettext('Investments') },
      { value: '2', text: this.$gettext('Operating expenses') },
      { value: '3', text: this.$gettext('Staff expenses') },
      { value: '4', text: this.$gettext('Other expenses') }
    ]
  }

  openDeleteModal () {
    this.deleteModalOpen = true
  }

  handleSaveOrEditClicked () {
    if (this.isEditMode) {
      this.saveBtnClicked()
    } else {
      this.toggleEditMode()
    }
  }

  handleDeleteOrCancelClicked () {
    if (this.isEditMode) {
      this.isEditMode = false
      if (this.newItem) {
        this.localFinancingNeedReportItem = null
      }
    } else {
      this.openDeleteModal()
    }
  }

  @Watch('newItem', { immediate: true })
  onNewItemChanged (val: boolean) {
    if (val || !this.isUpdate) {
      this.isEditMode = true
    } else {
      this.isEditMode = false
    }
  }

  @Watch('financingNeedReportItem', { immediate: true, deep: true })
  onFinancingNeedReportItemChanged () {
    if (this.newItem || !this.isUpdate) {
      this.isEditMode = true
    } else {
      this.isEditMode = false
    }
  }

  mounted () {
    if (!this.isUpdate || this.newItem) {
      this.isEditMode = true
    } else {
      this.existingReportFiles = [
        ...(this.financingNeedReportItem.report_files || []),
        ...(this.financingNeedReportItem.report_photos || [])]
    }
  }

  async saveBtnClicked () {
    if (this.isUpdate) {
      await this.updateFinancingNeed(this.financingNeedReportItem.id)
    } else {
      await this.createFinancingNeed(false)
    }
    this.isEditMode = !this.isEditMode
  }

  async toggleEditMode () {
    this.isEditMode = !this.isEditMode
  }

  confirmDelete () {
    if (this.isUpdate) {
      this.deleteFinancingNeed()
    } else {
      this.localFinancingNeedReportItem = null
      this.isEditMode = true
    }
  }

  deleteFinancingNeed (): void {
    const url = API_URLS.FINANCING_NEED_REPORT_ITEM.RETRIEVE(this.financingNeedReportItem.id) + '?own=true'
    axios.delete(url).then(() => {
      this.$emit("files-uploaded")
    }).catch(error => {
      this.showError(error)
    })
  }

  async onFileDeleted (): Promise<void> {
    this.existingReportFiles = []
  }

  setFinancingNeedReportItemFileUploadUrl (slug: string) {
    this.financingNeedReportItemFileUploadUrl = API_URLS.PHOTOS.UPLOAD_PHOTO(slug)
  }

  get disableInputs(): boolean {
    return !this.isEditMode || (!this.isEditMode && this.existingReportFiles.length > 0)
  }

  async updateFinancingNeed (id: string) {
    const url = API_URLS.FINANCING_NEED_REPORT_ITEM.RETRIEVE(id) + '?own=true'
    const data = {
      title: this.localFinancingNeedReportItem.title,
      description: this.localFinancingNeedReportItem.description,
      expense_type: this.localFinancingNeedReportItem.expense_type,
      expense_amount: this.localFinancingNeedReportItem.expense_amount,
      report_files: this.localFinancingNeedReportItem.report_files,
      report_photos: this.localFinancingNeedReportItem.report_photos,
      file_album_slug: this.financingNeedReportItem.file_album_slug
    }
    axios.put(url, data).then(response => {
      this.localFinancingNeedReportItem = response.data
      this.$emit('files-uploaded')
    }).catch(error => {
      this.showError(error)
    })
  }

  async createFinancingNeed (viaFileUploadBtn: boolean) {
    const url = API_URLS.FINANCING_NEED_REPORT_ITEM.LIST + '?own=true'
    const data = this.localFinancingNeedReportItem
    await axios.post(url, data).then(response => {
      this.setFinancingNeedReportItemFileUploadUrl(response.data.file_album_slug)
      this.localFinancingNeedReportItem = {
        title: response.data.title,
        description: response.data.description,
        expense_type: response.data.expense_type,
        expense_amount: response.data.expense_amount,
        report_files: response.data.report_files,
        report_photos: response.data.report_photos,
        file_album_slug: response.data.file_album_slug,
        result_report_slug: response.data.result_report_slug
      }
      this.userIsUploadingFile = true
      if (!viaFileUploadBtn) {
        this.$emit("item-created", response.data)
        this.resetForm()
      }
    }).catch(error => {
      this.showError(error)
    })
  }

  resetForm(): void {
    this.localFinancingNeedReportItem = {
      title: '',
      description: '',
      expense_type: 1,
      expense_amount: 0,
      report_files: [],
      report_photos: [],
      file_album_slug: '',
      result_report_slug: ''
    }
    this.existingReportFiles = []
  }

  async handleFileUpdated (files: IPhoto[]): Promise<void> {
    if (!files || files.length === 0) return
    files.forEach(file => {
      if (this.isImage(file)) {
        this.localFinancingNeedReportItem.report_photos.push(file)
      } else {
        this.localFinancingNeedReportItem.report_files.push(file)
      }
    })

    this.existingReportFiles = [
      ...this.localFinancingNeedReportItem.report_files,
      ...this.localFinancingNeedReportItem.report_photos
    ]
    if (this.userIsUploadingFile) {
      this.$emit("item-created", this.localFinancingNeedReportItem)
      this.userIsUploadingFile = false
    } else {
      this.$emit("files-uploaded")
    }
  }

  isImage(file: IPhoto): boolean {
    const path = file.src?.toLowerCase() || file.title?.toLowerCase() || ''
    return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(path)
  }

  async onFileUploadClicked (): Promise<void> {
    if (!this.isUpdate) {
      await this.createFinancingNeed(true)
    }
  }
}
